import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../../components/PageHero";
import { ScrollReveal } from "../../components/ScrollReveal";
import { Seo } from "../../components/Seo";
import { Alert } from "../../components/ui";
import { useLanguage } from "../../i18n/LanguageProvider";

/**
 * The GrowthWise Check-In and its feedback language.
 *
 * Everything happens in component state. There is no fetch, no analytics
 * call and no storage write anywhere on this page, which is what makes the
 * document's promise -- "completed in the user's browser and are not
 * transmitted or saved" -- literally true rather than merely stated.
 */

type Band = "lower" | "moderate" | "higher";

/**
 * Band thresholds.
 *
 * Six questions scored 0-3 give a 0-18 range. The source document names
 * three bands but does not define cut-points, so these thirds are an
 * explicit product assumption recorded in docs/open-questions.md (Q3), not
 * a validated scoring rule -- which is exactly why the page states it is
 * not a validated clinical instrument.
 */
const bandFor = (total: number): Band => {
  if (total <= 5) return "lower";
  if (total <= 11) return "moderate";
  return "higher";
};

export const CheckInPage = () => {
  const { t, tList, path } = useLanguage();

  const questions = tList("assessment.questions");
  const options = tList("assessment.options");

  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResult, setShowResult] = useState(false);
  const [attemptedIncomplete, setAttemptedIncomplete] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const answeredCount = Object.keys(answers).length;
  const complete = answeredCount === questions.length && questions.length > 0;

  const total = useMemo(
    () => Object.values(answers).reduce((sum, value) => sum + value, 0),
    [answers],
  );

  const band = bandFor(total);

  const handleSubmit = (): void => {
    if (!complete) {
      setAttemptedIncomplete(true);
      return;
    }
    setAttemptedIncomplete(false);
    setShowResult(true);
    // Move focus to the result so a screen-reader user is taken to the new
    // content rather than left on a button whose page has changed beneath it.
    window.requestAnimationFrame(() => resultRef.current?.focus());
  };

  const handleReset = (): void => {
    setAnswers({});
    setShowResult(false);
    setAttemptedIncomplete(false);
  };

  return (
    <>
      <Seo
        titleKey="meta.assessment.title"
        descriptionKey="meta.assessment.description"
      />

      <PageHero
        variant="checkin"
        eyebrow={t("assessment.eyebrow")}
        title={t("assessment.title")}
        subtitle={t("assessment.subtitle")}
      />

      <section className="gw-section">
        <div className="gw-container gw-container--narrow">
          <ScrollReveal>
            <p className="gw-lead">{t("assessment.intro")}</p>
          </ScrollReveal>

          {/* The non-clinical disclaimer is placed before the questions, not
              after the result, so it is read before anyone answers. */}
          <ScrollReveal delay={80}>
            <div style={{ marginTop: "1.5rem" }}>
              <Alert tone="warning" title={t("assessment.disclaimerHeading")}>
                {t("assessment.disclaimerBody")}
              </Alert>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div style={{ marginTop: "1rem" }}>
              <Alert tone="info">{t("assessment.privacyNotice")}</Alert>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="gw-section gw-section--sunken">
        <div className="gw-container gw-container--narrow">
          <h2 className="gw-title">{t("assessment.questionsHeading")}</h2>
          <p className="gw-note" aria-live="polite">
            {t("assessment.progressLabel", {
              answered: answeredCount,
              total: questions.length,
            })}
          </p>

          <div className="gw-progress" style={{ marginBlock: "1rem 2rem" }}>
            <div
              className="gw-progress__bar"
              style={{
                inlineSize: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%`,
              }}
            />
          </div>

          <div className="gw-stack-lg">
            {questions.map((question, questionIndex) => (
              <ScrollReveal
                key={question}
                delay={Math.min(questionIndex, 3) * 60}
              >
                {/*
                  A fieldset with a legend is what makes a scale accessible:
                  the question is announced once, then each option is
                  announced as part of that group rather than in isolation.
                */}
                <fieldset
                  style={{ border: 0, padding: 0, margin: 0 }}
                  className="gw-card"
                >
                  <legend
                    style={{
                      fontWeight: 600,
                      marginBottom: "0.9rem",
                      fontSize: "var(--gw-text-base)",
                    }}
                  >
                    <span className="gw-card__index">{questionIndex + 1}</span>{" "}
                    {question}
                  </legend>

                  <div className="gw-scale">
                    {options.map((option, optionIndex) => (
                      <label key={option} className="gw-scale__option">
                        <input
                          type="radio"
                          name={`question-${questionIndex}`}
                          value={optionIndex}
                          checked={answers[questionIndex] === optionIndex}
                          onChange={() => {
                            setAnswers((current) => ({
                              ...current,
                              [questionIndex]: optionIndex,
                            }));
                            setShowResult(false);
                          }}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </ScrollReveal>
            ))}
          </div>

          {attemptedIncomplete ? (
            <div style={{ marginTop: "1.5rem" }}>
              <Alert tone="danger">{t("assessment.incomplete")}</Alert>
            </div>
          ) : null}

          <div className="gw-form-actions">
            <button
              type="button"
              className="gw-btn gw-btn--primary"
              onClick={handleSubmit}
            >
              {t("assessment.submit")}
            </button>
            <button
              type="button"
              className="gw-btn gw-btn--secondary"
              onClick={handleReset}
            >
              {t("assessment.reset")}
            </button>
          </div>
        </div>
      </section>

      {showResult ? (
        <section className="gw-section">
          <div className="gw-container gw-container--narrow">
            <div ref={resultRef} tabIndex={-1} style={{ outline: "none" }}>
              <p className="gw-eyebrow">{t("assessment.resultEyebrow")}</p>
              <h2 className="gw-title">{t("assessment.resultTitle")}</h2>
              <p className="gw-subtitle">{t("assessment.resultSubtitle")}</p>

              <div
                className="gw-card gw-result-band"
                style={{ marginTop: "2rem" }}
              >
                <span className="gw-badge gw-badge--info">
                  {t(`assessment.bands.${band}.label`)}
                </span>
                <h3
                  style={{
                    fontSize: "var(--gw-text-lg)",
                    marginBlock: "0.9rem 0.6rem",
                  }}
                >
                  {t(`assessment.bands.${band}.headline`)}
                </h3>
                <p style={{ color: "var(--gw-ink-muted)" }}>
                  {t(`assessment.bands.${band}.body`)}
                </p>

                <hr className="gw-divider" />

                <p className="gw-eyebrow">{t("assessment.nextStepLabel")}</p>
                <p style={{ marginTop: "0.5rem" }}>
                  {t(`assessment.bands.${band}.nextStep`)}
                </p>
              </div>

              {/* Repeated after the result as well as before the questions:
                  this is the moment someone is most likely to over-read the
                  outcome. */}
              <div style={{ marginTop: "1.5rem" }}>
                <Alert tone="warning" title={t("assessment.disclaimerHeading")}>
                  {t("assessment.disclaimerBody")}
                </Alert>
              </div>

              <div style={{ marginTop: "1.5rem" }}>
                <Alert tone="info" title={t("footer.crisisHeading")}>
                  {t("footer.crisisBody")}
                </Alert>
              </div>

              <div className="gw-form-actions">
                <Link to={path("/contact")} className="gw-btn gw-btn--primary">
                  {t("home.ctaButton")}
                </Link>
                <Link
                  to={path("/services")}
                  className="gw-btn gw-btn--secondary"
                >
                  {t("home.servicesLink")}
                </Link>
                <button
                  type="button"
                  className="gw-btn gw-btn--ghost"
                  onClick={handleReset}
                >
                  {t("assessment.reset")}
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};
